import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PlusIcon, TrashIcon } from '@/components/icons';

function formatTargetDisplay(target: string, targetOptions?: TargetOption[]): string {
  const envMatch = target.match(/^environment_config\.env\.(.+)$/);
  if (envMatch) return envMatch[1];

  if (targetOptions) {
    for (const g of targetOptions) {
      const opt = g.options.find((o) => o.value === target);
      if (opt) return opt.label;
    }
  }

  return target;
}

export interface ParamItem {
  type: string;
  name: string;
  target: string;
  value: string;
  valueType: string;
  configurable: boolean;
}

export interface TargetOption {
  group: string;
  options: { value: string; label: string; dataType?: string }[];
}

interface ParamConfigEditorProps {
  params: ParamItem[];
  onChange: (params: ParamItem[]) => void;
  targetOptions?: TargetOption[];
  targetTypeMap?: Record<string, string>;
  paramTypes?: string[];
  emptyText?: string;
  addButtonText?: string;
  allowAdd?: boolean;
  readonlyTarget?: boolean;
}

export default function ParamConfigEditor({
  params,
  onChange,
  targetOptions,
  targetTypeMap,
  paramTypes = ['string', 'number', 'boolean', 'object', 'array'],
  emptyText = '暂无参数',
  addButtonText = '添加参数',
  allowAdd = true,
  readonlyTarget = false,
}: ParamConfigEditorProps) {
  const add = () =>
    onChange([
      ...params,
      { type: 'string', name: '', target: '', value: '', valueType: 'value', configurable: true },
    ]);

  const remove = (i: number) => onChange(params.filter((_, idx) => idx !== i));

  const update = (i: number, field: keyof ParamItem, val: string) => {
    const next = [...params];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };

  const handleTargetChange = (i: number, val: string) => {
    const next = [...params];
    const inferred = targetTypeMap?.[val];
    next[i] = { ...next[i], target: val, type: inferred || next[i].type };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {params.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 px-4 rounded-xl border-2 border-dashed border-border/60 bg-muted/20">
          <span className="text-sm text-muted-foreground mb-2">{emptyText}</span>
          {allowAdd ? (
            <>
              <span className="text-xs text-muted-foreground/70 mb-4">点击下方按钮添加参数</span>
              <button
                type="button"
                onClick={add}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-border/60 text-sm text-foreground hover:border-primary/50 hover:bg-primary/5 transition-all"
              >
                <PlusIcon className="h-4 w-4" />
                {addButtonText}
              </button>
            </>
          ) : (
            <span className="text-xs text-muted-foreground/70">
              请通过字段旁的「可配置」开关启用参数
            </span>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {params.map((param, i) => (
              <ParamCard
                key={i}
                index={i}
                param={param}
                targetOptions={targetOptions}
                paramTypes={paramTypes}
                readonlyTarget={readonlyTarget}
                onUpdate={update}
                onUpdateTarget={handleTargetChange}
                onRemove={remove}
              />
            ))}
          </div>
          {allowAdd && (
            <button
              type="button"
              onClick={add}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-border/60 text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 hover:bg-primary/5 transition-all"
            >
              <PlusIcon className="h-3 w-3" />
              {addButtonText}
            </button>
          )}
        </>
      )}
    </div>
  );
}

interface ParamCardProps {
  index: number;
  param: ParamItem;
  targetOptions?: TargetOption[];
  paramTypes: string[];
  readonlyTarget?: boolean;
  onUpdate: (i: number, field: keyof ParamItem, val: string) => void;
  onUpdateTarget: (i: number, val: string) => void;
  onRemove: (i: number) => void;
}

function ParamCard({
  index,
  param,
  targetOptions,
  paramTypes,
  readonlyTarget = false,
  onUpdate,
  onUpdateTarget,
  onRemove,
}: ParamCardProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border border-border bg-background overflow-hidden shadow-sm">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-mono text-muted-foreground w-5 shrink-0">#{index + 1}</span>
          <span className="text-sm font-medium truncate">{param.name || '未命名参数'}</span>
          {param.name && (
            <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-mono bg-muted text-muted-foreground border border-border">
              {param.type}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
            className="p-1 text-muted-foreground/40 hover:text-destructive transition-colors"
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </button>
          <span
            className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border/50 bg-muted/10">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">参数名</Label>
              <Input
                placeholder="例如: cpu_limit"
                value={param.name}
                onChange={(e) => onUpdate(index, 'name', e.target.value)}
                className="h-8 text-sm font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">类型</Label>
              <select
                value={param.type}
                disabled={!!targetOptions}
                onChange={(e) => onUpdate(index, 'type', e.target.value)}
                className={`h-8 w-full px-2 rounded-md border border-input text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  targetOptions
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : 'bg-background'
                }`}
              >
                {paramTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">目标字段</Label>
            {readonlyTarget ? (
              <div className="h-8 w-full px-2 rounded-md border border-input bg-muted/50 text-sm flex items-center">
                <span className="text-muted-foreground font-mono text-xs truncate">
                  {formatTargetDisplay(param.target, targetOptions)}
                </span>
              </div>
            ) : targetOptions ? (
              <select
                value={param.target}
                onChange={(e) => onUpdateTarget(index, e.target.value)}
                className="h-8 w-full px-2 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">选择目标字段...</option>
                {targetOptions.map((g) => (
                  <optgroup key={g.group} label={g.group}>
                    {g.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            ) : (
              <Input
                placeholder="例如: resource_config.cpu_milli_cores"
                value={param.target}
                onChange={(e) => onUpdate(index, 'target', e.target.value)}
                className="h-8 text-sm font-mono"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">默认值</Label>
            <Input
              placeholder="默认值"
              value={param.value}
              onChange={(e) => onUpdate(index, 'value', e.target.value)}
              className="h-8 text-sm"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id={`configurable-${index}`}
              checked={param.configurable}
              onChange={(e) => onUpdate(index, 'configurable', e.target.checked.toString())}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <Label
              htmlFor={`configurable-${index}`}
              className="text-xs text-muted-foreground cursor-pointer"
            >
              允许实例化时配置
            </Label>
          </div>
        </div>
      )}
    </div>
  );
}
